import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { Box } from '@saviynt/design-system';

import { RiskGraph, SodReloader } from '../../../../shared/components';
import { useTypedSelector } from '../../../../shared/hooks';
import { type ActiveGraphPointState } from '../../../../shared/types';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { getFilterPayload, getPoinSize } from '../../utils';

import './ViolationsTabGraph.css';

interface PlotDataState {
  maxViolationAge: number;
  maxViolationScore: number;
  totalViolations: number;
  timeRange: string;
  updatedTime: string;
  clusters: ActiveGraphPointState[];
}

/**
 * ViolationsTabGraph
 */
function ViolationsTabGraph(): ReactElement {
  const dispatch = useDispatch();
  const {
    activeFilters,
    tabInAction,
    searchKey,
    activeInsight,
    activeGraphPoint,
  } = useTypedSelector((state) => state.aag.violationsTab.controls);

  const [isFirstTime, setIsFirstTime] = useState(false);
  const [data, setData] = useState<PlotDataState | null>({
    clusters: [] as ActiveGraphPointState[],
  } as PlotDataState);

  const abortRef = useRef<((reason: string) => void) | null>(null);
  const [fetchGraphPoints, { isError, isLoading }] =
    violationsTabActions.useGetViolationsGraphMutation();

  const handleFetchGraphPoints = useCallback(async () => {
    abortRef.current?.('New Search');

    const activeFiltersMap = Object.fromEntries(
      activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
    );

    const filters = {
      ...getFilterPayload(
        activeFiltersMap,
        tabInAction ?? undefined,
        searchKey
      ),
      insightRequests:
        activeInsight !== null
          ? [
              {
                id: activeInsight.id,
                filters: activeInsight.filters,
              },
            ]
          : undefined,
    };

    try {
      const controller = fetchGraphPoints({
        filters,
      });

      abortRef.current = () => controller.abort();

      const response = await controller.unwrap();

      return response;
    } catch {
      throw Error('Failed graph API');
    }
  }, [activeFilters, tabInAction, searchKey, activeInsight, fetchGraphPoints]);

  const triggerFetchGraphPoints = useCallback(() => {
    handleFetchGraphPoints()
      .then((response) => {
        const typedResponse = response as PlotDataState;

        setData(typedResponse);
        setIsFirstTime(true);
      })
      .catch((error) => {
        Logger.log('Error having graph', error);
      });
  }, [handleFetchGraphPoints]);

  useEffect(() => {
    triggerFetchGraphPoints();
  }, [triggerFetchGraphPoints]);

  const severityMap = useMemo(() => {
    const severityOrder = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

    const countBySeverity = severityOrder.reduce<Record<string, number>>(
      (acc, severity) => {
        if (data === null) {
          return acc;
        }

        const total = data.clusters
          .filter((item) => item.severity === severity)
          .reduce((sum, item) => sum + item.count, 0);

        if (total > 0) {
          acc[severity] = total;
        }

        return acc;
      },
      {}
    );

    return countBySeverity;
  }, [data]);

  const renderBody = useCallback(() => {
    if (isError || data === null) {
      setIsFirstTime(false);

      return <SodReloader triggerRefresh={() => triggerFetchGraphPoints()} />;
    }

    if (isFirstTime) {
      return (
        <Box className='Sod-ViolationsTabGraph-riskGraphHolder'>
          <RiskGraph
            plotPoints={data.clusters.map((point) => ({
              id: `${tabInAction ?? ''}--||--${point.clusterId}`,
              xIn: point.violationAge,
              yIn: point.violationScore,
              size: getPoinSize(point.count),
              isActive: activeGraphPoint?.clusterId === point.clusterId,
              category: point.severity,
            }))}
            centerCount={data.totalViolations}
            chartScales={{
              x_min: 0,
              x_max: data.maxViolationAge,
              y_min: 0,
              y_max: data.maxViolationScore,
            }}
            onPointClick={(pointId) => {
              const targetClusterId = (pointId.id as string).split('--||--')[1];

              const targetCluster = data.clusters.find(
                (cluster) => cluster.clusterId === targetClusterId
              );

              dispatch(
                violationsTabActions.setActiveGraphPoint(targetCluster ?? null)
              );
            }}
            isPointHoverDisabled={activeInsight !== null}
            filterCategories={Object.keys(severityMap).reverse()}
            filterTooltips={Object.keys(severityMap)
              .map((severity) => {
                const count = severityMap[severity];

                return `${count} ${
                  String(severity).charAt(0).toUpperCase() +
                  String(severity).slice(1).toLowerCase()
                } Violations`;
              })
              .reverse()}
            isAnimation
            isProcessing={isLoading}
            isSectorInteractive={false}
          />
        </Box>
      );
    }

    return null;
  }, [
    dispatch,
    isError,
    isLoading,
    data,
    isFirstTime,
    severityMap,
    tabInAction,
    activeInsight,
    activeGraphPoint,
    triggerFetchGraphPoints,
  ]);

  return <Box className='Sod-ViolationsTabGraph'>{renderBody()}</Box>;
}

export default memo(ViolationsTabGraph);
